/**
 * MobileAccountPage — the "Me" tab.
 *
 * Deliberately not a phone port of MyAccountPage. The phone build carries Jobs
 * and Bookings only, so this screen exists for the things the shell has to
 * expose somewhere: who is signed in, how to sign out, how to install the app,
 * and where to get help. Account editing stays on the desktop.
 */

import React from 'react';
import { LogOut, Wifi, WifiOff } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout, logoutUser, selectUser } from '../../store/slices/authSlice';
import useOnlineStatus from '../../pwa/useOnlineStatus';
import { isStandalone } from '../../pwa/useInstallPrompt';
import { InstallCard } from '../pwa/PwaBanners';

const Row = ({ label, value }) => (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-50 last:border-b-0">
        <span className="text-xs text-gray-400 shrink-0">{label}</span>
        <span className="text-sm font-medium text-gray-800 truncate text-right">{value || '—'}</span>
    </div>
);

const MobileAccountPage = () => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const online = useOnlineStatus();

    const handleSignOut = () => {
        // Both dispatches, matching Layout: logoutUser revokes server-side,
        // logout clears local state even if that call fails.
        dispatch(logoutUser());
        dispatch(logout());
    };

    return (
        <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Identity */}
            <div className="bg-white border-b border-gray-100 px-4 py-5 flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-emerald-700 font-semibold text-lg">
                        {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user?.email || 'User'}</p>
                    <p className="text-xs text-gray-500">{user?.staffRole || user?.userType || 'Owner'}</p>
                </div>
            </div>

            <InstallCard />

            {/* Status */}
            <div className="mx-4 mt-3 bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50">
                    {online
                        ? <Wifi size={15} className="text-emerald-500 shrink-0" />
                        : <WifiOff size={15} className="text-amber-500 shrink-0" />}
                    <span className={`text-sm font-medium ${online ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {online ? 'Connected' : 'No connection'}
                    </span>
                </div>
                <Row label="Mode" value={isStandalone() ? 'Installed app' : 'Browser tab'} />
            </div>

            {/* Actions.
                No Help row: HelpPage's contact buttons are inert placeholders
                and the app carries no real support address anywhere, so a
                mailto here would be an invented contact route. Add one when
                there is a genuine destination to point at. */}
            <div className="mx-4 mt-3 mb-6 bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-red-50 min-h-[52px]
                               bg-transparent border-none cursor-pointer text-left"
                >
                    <LogOut size={17} className="text-red-500 shrink-0" />
                    <span className="text-sm font-medium text-red-600">Sign out</span>
                </button>
            </div>

            <p className="text-center text-[11px] text-gray-300 pb-6">
                Full workshop management is available on a desktop or tablet.
            </p>
        </div>
    );
};

export default MobileAccountPage;
